import { imax, mulBp } from '../math/fixed.ts';
import { hexToWorld } from '../hex.ts';
import { cellAtWorld, touchGrid } from '../grid.ts';
import type { BattleState } from '../types.ts';
import { damageHero, ev } from './combat.ts';
import { climbShake } from './hoist.ts';

/**
 * The titan's neck (El Párpado 1, docs/SAGAS.md): a gorge of skin whose folds breathe. Down its descent the doors
 * between rooms are folds: open on the out-breath, shut on the in-breath, and one shutting on a hero squeezes them
 * (hurt) back into the room behind. And now and then the titan shakes in its sleep: the dust jumps off the skin a
 * moment ahead, and whoever is walking when it hits falls (hurt, down a moment); whoever stood still keeps their feet.
 * The shake is the rule on the Order's post too (a field, no folds).
 */

export const neckTick = (s: BattleState): void => {
  const r = s.rules?.neck;
  if (!r) return;
  const st = (s.neck ??= { open: true, shake: r.shake.first, warned: false });
  if (!s.started) return;
  // The breath (only down the descent: its doors are the folds).
  const doors = s.map.dungeon?.doors ?? [];
  if (doors.length) {
    const open = s.waveT % (r.open + r.shut) < r.open;
    if (open !== st.open) {
      st.open = open;
      ev(s, { t: 'neck', kind: open ? 'out' : 'in', x: 0, y: 0 });
      if (!open) {
        // Shutting: whoever is in a fold is squeezed back into the room behind it.
        doors.forEach((d, i) => {
          const cell = cellAtWorld(s, hexToWorld(d.q, d.r).x, hexToWorld(d.q, d.r).y);
          const back = s.map.dungeon?.rooms[i];
          if (!back) return;
          const to = hexToWorld(back.q, back.r);
          for (const h of s.heroes) {
            if (h.downTicks > 0 || cellAtWorld(s, h.x, h.y) !== cell) continue;
            damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.squeezeBp)), null);
            h.x = to.x;
            h.y = to.y;
            h.moving = false;
            h.path = [];
            ev(s, { t: 'neck', kind: 'squeeze', x: h.x, y: h.y });
          }
        });
      }
      touchGrid(s);
    }
  }
  // The shake: dust a moment ahead, then whoever is walking falls.
  if (!st.warned && s.waveT >= st.shake - r.shake.warn) {
    st.warned = true;
    ev(s, { t: 'neck', kind: 'dust', x: 0, y: 0 });
  }
  if (s.waveT < st.shake) return;
  st.shake = s.waveT + r.shake.every;
  st.warned = false;
  titanShake(s);
};

/** The titan shakes: whoever is walking falls (on the bell's climb, the bell slips back unless someone braces it). */
export const titanShake = (s: BattleState): void => {
  const r = s.rules?.neck;
  if (!r) return;
  ev(s, { t: 'neck', kind: 'shake', x: 0, y: 0 });
  climbShake(s);
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !h.moving) continue;
    damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.shake.hitBp)), null);
    h.stun = imax(h.stun, r.shake.down);
    h.moving = false;
    h.path = [];
    ev(s, { t: 'neck', kind: 'fall', x: h.x, y: h.y });
  }
};

/** Whether the folds stand open (the out-breath); always, away from the neck. */
export const neckOpen = (s: BattleState): boolean => !s.rules?.neck || !s.neck || s.neck.open;
